import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DEFAULT_PAGING, DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { Version } from '../../models/version.model';
import {
  buildVersionAttributeFiltersPayload,
  DisplayColor,
  mapPlainVersionsToAttributeSearchResults,
  resolveFilterFieldDisplayName,
  StoreAttributeFilterCategoryViewModel,
  StoreAttributeFilterFieldViewModel,
  StoreAttributeValueType,
  StoreSourceSelectOption,
  VersionAttributeFilterSelection,
  VersionAttributeFilterPair,
  VersionAttributeSearchResult,
  VersionAttributeSearchSummary,
} from '../../models/store-attribute.model';
import { StoreAttributeService } from '../../services/store-attribute.service';
import {
  VersionAssociationEntityType,
  VersionAssociationFilterService,
} from '../../services/version-association-filter.service';
import { VersionOfferService } from '../../services/version-offer.service';
import { VersionPromoService } from '../../services/version-promo.service';

@Component({
  selector: 'app-version-entity-association-attribute-search',
  templateUrl: './version-entity-association-attribute-search.component.html',
  styleUrls: ['./version-entity-association-attribute-search.component.scss'],
})
export class VersionEntityAssociationAttributeSearchComponent implements OnInit, OnDestroy {
  @Input() eventId = '';
  @Input() entityType: VersionAssociationEntityType = 'offer';
  @Input() versionCodeSearch = '';
  @Input() selectedVersionNames: string[] = [];
  @Input() includePartialMatches = false;

  @Output() filtersChange = new EventEmitter<VersionAttributeFilterSelection[]>();
  @Output() selectedVersionNamesChange = new EventEmitter<string[]>();
  @Output() includePartialMatchesChange = new EventEmitter<boolean>();
  @Output() searchResultsChange = new EventEmitter<VersionAttributeSearchResult[]>();

  readonly booleanType: StoreAttributeValueType = 'Boolean';
  readonly textType: StoreAttributeValueType = 'Text';
  readonly singleSelectType: StoreAttributeValueType = 'SingleSelect';
  readonly multiSelectType: StoreAttributeValueType = 'MultiSelect';
  readonly simpleDisplayedColumns = ['select', 'versionName'] as const;

  categories: StoreAttributeFilterCategoryViewModel[] = [];
  categoriesLoading = true;
  categoriesLoadError = false;
  filterForm = new FormGroup({});
  activeSelections: VersionAttributeFilterSelection[] = [];
  appliedSelections: VersionAttributeFilterSelection[] = [];
  hasAppliedFilters = false;
  fieldByClientKey = new Map<string, StoreAttributeFilterFieldViewModel>();
  apiResults: VersionAttributeSearchResult[] = [];
  apiTotal = 0;
  attributeSearchLoading = false;
  expandedConflictVersionId: string | null = null;
  appliedFilterPairs: VersionAttributeFilterPair[] = [];
  page: PageEvent = { ...DEFAULT_PAGING };

  private formSubscription?: Subscription;
  private categoriesSubscription?: Subscription;
  private searchSubscriptions: Subscription[] = [];
  private attributeModeInitialized = false;

  constructor(
    private storeAttributeService: StoreAttributeService,
    private associationFilterService: VersionAssociationFilterService,
    private versionOfferService: VersionOfferService,
    private versionPromoService: VersionPromoService,
    private matSnackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.categoriesSubscription = this.storeAttributeService
      .getFilterEnabledAttributesState()
      .subscribe((state) => {
        this.categoriesLoading = state.loading;
        this.categoriesLoadError = state.loadFailed;
        if (state.loading) {
          return;
        }
        this.categories = state.categories;
        this.buildFilterForm(state.categories);
        if (this.hasStoreAttributes) {
          this.initAttributeMode();
        } else {
          this.initSimpleMode();
        }
      });
  }

  get hasStoreAttributes(): boolean {
    return !this.categoriesLoading && this.categories.length > 0;
  }

  ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
    this.categoriesSubscription?.unsubscribe();
    this.searchSubscriptions.forEach((sub) => sub.unsubscribe());
    this.associationFilterService.reset();
  }

  get hasActiveFilters(): boolean {
    return this.activeSelections.length > 0;
  }

  get hasAppliedAttributeFilters(): boolean {
    return this.appliedSelections.length > 0;
  }

  get hasVersionCodeSearch(): boolean {
    return (this.versionCodeSearch ?? '').trim().length > 0;
  }

  get canSearch(): boolean {
    return this.hasActiveFilters || this.hasVersionCodeSearch;
  }

  get searchSummary(): VersionAttributeSearchSummary {
    const matchingVersions = this.apiTotal || this.apiResults.length;
    const fullMatch = this.apiResults.filter((result) => result.matchType === 'full').length;
    const partialMatch = this.apiResults.filter((result) => result.matchType === 'partial').length;

    return {
      matchingVersions,
      fullMatch,
      partialMatch,
      totalStores: this.apiResults.reduce((sum, result) => sum + result.storeCount, 0),
    };
  }

  get filteredResultsTitle(): string {
    if (this.appliedSelections.length) {
      return `Filtered Versions — ${this.appliedSelections.map((s) => s.displayValue).join(' / ')}`;
    }
    if (this.hasVersionCodeSearch) {
      return `Filtered Versions — "${(this.versionCodeSearch ?? '').trim()}"`;
    }
    return 'Filtered Versions';
  }

  get fullMatchResults(): VersionAttributeSearchResult[] {
    return this.apiResults.filter((r) => r.matchType === 'full');
  }

  get partialMatchResults(): VersionAttributeSearchResult[] {
    return this.apiResults.filter((r) => r.matchType === 'partial');
  }

  get allFullMatchesSelected(): boolean {
    const fullNames = this.fullMatchResults.map((r) => r.versionName);
    return fullNames.length > 0 && fullNames.every((name) => this.selectedVersionNames.includes(name));
  }

  get someFullMatchesSelected(): boolean {
    const fullNames = this.fullMatchResults.map((r) => r.versionName);
    const selectedCount = fullNames.filter((name) => this.selectedVersionNames.includes(name)).length;
    return selectedCount > 0 && selectedCount < fullNames.length;
  }

  getControl(clientKey: string): FormControl {
    return this.filterForm.get(clientKey) as FormControl;
  }

  getSelectOptions(field: StoreAttributeFilterFieldViewModel): StoreSourceSelectOption[] {
    if (field.selectOptions?.length) {
      return field.selectOptions;
    }
    return field.options.map((option) => ({ value: option, label: option }));
  }

  getFilterFieldDisplayName(field: StoreAttributeFilterFieldViewModel): string {
    return resolveFilterFieldDisplayName(field);
  }

  getOptionLabel(field: StoreAttributeFilterFieldViewModel, value: unknown): string {
    const stringValue = String(value);
    const match = this.getSelectOptions(field).find((option) => option.value === stringValue);
    return match?.label ?? stringValue;
  }

  isVersionSelected(versionName: string): boolean {
    return this.selectedVersionNames.includes(versionName);
  }

  isAllSimpleModeSelected(): boolean {
    return this.apiResults.length > 0
      && this.apiResults.every((r) => this.isVersionSelected(r.versionName));
  }

  someSimpleModeSelected(): boolean {
    const selectedOnPage = this.apiResults.filter((r) => this.isVersionSelected(r.versionName)).length;
    return selectedOnPage > 0 && selectedOnPage < this.apiResults.length;
  }

  onSimpleModeMasterToggle(): void {
    if (this.isAllSimpleModeSelected()) {
      this.emitSelectedVersionNames([]);
    } else {
      this.emitSelectedVersionNames(this.apiResults.map((r) => r.versionName));
    }
  }

  isRowSelectable(result: VersionAttributeSearchResult): boolean {
    if (!this.hasStoreAttributes) {
      return true;
    }
    return result.matchType === 'full' || this.includePartialMatches;
  }

  applySimpleSearch(search: string): void {
    if (this.hasStoreAttributes) {
      return;
    }
    switch (this.entityType) {
      case 'promo':
        this.versionPromoService.dialogSearch(search);
        break;
      case 'offerGroup':
        this.versionOfferService.offerGdialogsearch(search);
        break;
      default:
        this.versionOfferService.dialogSearch(search);
        break;
    }
  }

  onPage(pageEvent: PageEvent): void {
    if (this.hasStoreAttributes) {
      return;
    }
    this.emitSelectedVersionNames([]);
    switch (this.entityType) {
      case 'promo':
        this.versionPromoService.versionsNotInPromoPage(pageEvent);
        break;
      case 'offerGroup':
        this.versionOfferService.offerGDialogpage(pageEvent);
        break;
      default:
        this.versionOfferService.versionsNotInOfferPage(pageEvent);
        break;
    }
  }

  onVersionCheckboxChange(result: VersionAttributeSearchResult, checked: boolean): void {
    if (!this.isRowSelectable(result)) {
      return;
    }
    const next = checked
      ? [...this.selectedVersionNames, result.versionName]
      : this.selectedVersionNames.filter((name) => name !== result.versionName);
    this.emitSelectedVersionNames([...new Set(next)]);
  }

  onSelectAllFullMatchesChange(checked: boolean): void {
    const fullNames = this.fullMatchResults.map((r) => r.versionName);
    const partialNames = this.partialMatchResults.map((r) => r.versionName);

    if (checked) {
      const partialKept = this.includePartialMatches
        ? this.selectedVersionNames.filter((name) => partialNames.includes(name))
        : [];
      this.emitSelectedVersionNames([...fullNames, ...partialKept]);
      return;
    }

    this.emitSelectedVersionNames(this.selectedVersionNames.filter((name) => !fullNames.includes(name)));
  }

  onIncludePartialMatchesChange(checked: boolean): void {
    this.includePartialMatches = checked;
    this.includePartialMatchesChange.emit(checked);

    const partialNames = this.partialMatchResults.map((r) => r.versionName);

    if (checked) {
      this.emitSelectedVersionNames([...new Set([...this.selectedVersionNames, ...partialNames])]);
      return;
    }

    const partialNameSet = new Set(partialNames);
    this.emitSelectedVersionNames(this.selectedVersionNames.filter((name) => !partialNameSet.has(name)));
  }

  removeFilter(clientKey: string): void {
    const field = this.fieldByClientKey.get(clientKey);
    if (!field) {
      return;
    }
    this.getControl(clientKey)?.setValue(this.defaultValueForField(field));
  }

  clearAll(): void {
    this.fieldByClientKey.forEach((field, clientKey) => {
      this.getControl(clientKey)?.setValue(this.defaultValueForField(field), { emitEvent: false });
    });
    this.activeSelections = [];
    this.appliedSelections = [];
    this.hasAppliedFilters = false;
    this.appliedFilterPairs = [];
    this.expandedConflictVersionId = null;
    this.includePartialMatches = false;
    this.includePartialMatchesChange.emit(false);
    this.associationFilterService.setAttributeFilterPairs([]);
    this.filtersChange.emit([]);
    this.emitSelectedVersionNames([]);
  }

  /** Re-run search with updated version code text while keeping the grid visible. */
  refreshVersionCodeSearch(versionCodeSearch: string): void {
    if (!this.hasAppliedFilters) {
      return;
    }

    const pairs = buildVersionAttributeFiltersPayload(
      this.filterForm.getRawValue(),
      this.fieldByClientKey.values(),
    );

    this.appliedFilterPairs = pairs;
    this.expandedConflictVersionId = null;

    this.associationFilterService.setVersionCodeSearch(versionCodeSearch);
    this.associationFilterService.setAttributeFilterPairs(pairs);
    this.associationFilterService.reload();
  }

  applyFilters(): void {
    if (!this.canSearch) {
      return;
    }

    this.appliedSelections = [...this.activeSelections];
    this.hasAppliedFilters = true;

    const pairs = buildVersionAttributeFiltersPayload(
      this.filterForm.getRawValue(),
      this.fieldByClientKey.values(),
    );

    this.appliedFilterPairs = pairs;
    this.expandedConflictVersionId = null;

    this.associationFilterService.setVersionCodeSearch(this.versionCodeSearch);
    this.associationFilterService.setAttributeFilterPairs(pairs);
    this.associationFilterService.reload();
    this.filtersChange.emit(this.activeSelections);
  }

  /** Re-run search with updated version code search text (parent Search button). */
  rerunSearch(): void {
    if (!this.hasAppliedFilters || !this.canSearch) {
      return;
    }
    this.applyFilters();
  }

  categoryUsesOnlyBooleans(category: StoreAttributeFilterCategoryViewModel): boolean {
    return category.fields.length > 0 && category.fields.every((field) => field.valueType === this.booleanType);
  }

  categoryUsesOnlySelects(category: StoreAttributeFilterCategoryViewModel): boolean {
    return category.fields.length > 0 && category.fields.every((field) => field.valueType === this.singleSelectType);
  }

  categoryUsesOnlyMultiSelects(category: StoreAttributeFilterCategoryViewModel): boolean {
    return category.fields.length > 0 && category.fields.every((field) => field.valueType === this.multiSelectType);
  }

  getCategoryDotClass(color: DisplayColor): string {
    const map: Record<DisplayColor, string> = {
      blue: 'dot-geo',
      red: 'dot-alc',
      green: 'dot-health',
      orange: 'dot-fresh',
      purple: 'dot-svc',
      teal: 'dot-svc',
      brown: 'dot-svc',
      grey: 'dot-svc',
    };
    return map[color] ?? 'dot-geo';
  }

  getCategoryTagClass(color: DisplayColor): string {
    const map: Record<DisplayColor, string> = {
      blue: 'ftag-geo',
      red: 'ftag-alc',
      green: 'ftag-health',
      orange: 'ftag-fresh',
      purple: 'ftag-svc',
      teal: 'ftag-svc',
      brown: 'ftag-svc',
      grey: 'ftag-geo',
    };
    return map[color] ?? 'ftag-geo';
  }

  getTagClass(colorHex: string): string {
    if (colorHex.includes('1976d2') || colorHex.includes('2196f3')) return 'tag-geo';
    if (colorHex.includes('d32f2f') || colorHex.includes('f44336')) return 'tag-alc';
    if (colorHex.includes('388e3c') || colorHex.includes('4caf50')) return 'tag-health';
    if (colorHex.includes('f57c00') || colorHex.includes('ff9800')) return 'tag-fresh';
    if (colorHex.includes('7b1fa2') || colorHex.includes('9c27b0')) return 'tag-svc';
    return 'tag-geo';
  }

  formatTagLabel(tag: { label: string; value: string; coveragePercent?: number }): string {
    if (tag.coveragePercent != null && tag.coveragePercent < 100) {
      return `${tag.label} (${tag.coveragePercent}%)`;
    }
    return tag.label;
  }

  getMatchTooltip(result: VersionAttributeSearchResult): string {
    if (result.matchType === 'full') {
      return `Full match: All ${result.storeCount} stores match all active filter criteria`;
    }
    return `Partial match: ${result.matchPercent}% of stores match all active filter criteria`;
  }

  toggleConflictDetail(result: VersionAttributeSearchResult, event?: Event): void {
    event?.stopPropagation();
    if (!result.versionId || !this.hasAppliedFilters) {
      return;
    }

    this.expandedConflictVersionId =
      this.expandedConflictVersionId === result.versionId ? null : result.versionId;
  }

  isConflictDetailExpanded(result: VersionAttributeSearchResult): boolean {
    return !!result.versionId && this.expandedConflictVersionId === result.versionId;
  }

  onConflictExclude(result: VersionAttributeSearchResult): void {
    this.expandedConflictVersionId = null;
    this.emitSelectedVersionNames(this.selectedVersionNames.filter((name) => name !== result.versionName));
  }

  onConflictIncludeAnyway(result: VersionAttributeSearchResult): void {
    this.includePartialMatches = true;
    this.includePartialMatchesChange.emit(true);
    if (!this.selectedVersionNames.includes(result.versionName)) {
      this.emitSelectedVersionNames([...this.selectedVersionNames, result.versionName]);
    }
    this.expandedConflictVersionId = null;
  }

  private emitSelectedVersionNames(names: string[]): void {
    this.selectedVersionNames = names;
    this.selectedVersionNamesChange.emit(names);
  }

  private initAttributeMode(): void {
    if (this.attributeModeInitialized) {
      return;
    }
    this.attributeModeInitialized = true;

    this.searchSubscriptions.push(
      this.associationFilterService.attributeSearchResults$.subscribe((results) => {
        this.apiResults = results;
        this.searchResultsChange.emit(results);
      }),
      this.associationFilterService.attributeSearchTotal$.subscribe((total) => {
        this.apiTotal = total;
      }),
      this.associationFilterService.attributeSearchLoading$.subscribe((loading) => {
        this.attributeSearchLoading = loading;
      }),
      this.associationFilterService.attributeSearchError$
        .pipe(filter((message): message is string => !!message))
        .subscribe((message) => {
          this.matSnackBar.open(message, 'OK', DEFAULT_SNACKBAR_CONFIG);
        }),
    );
  }

  private initSimpleMode(): void {
    this.hasAppliedFilters = true;

    switch (this.entityType) {
      case 'promo':
        this.versionPromoService.versionsNotInPromoResetpage();
        this.versionPromoService.dialogSearch('');
        this.versionPromoService.versionsNotInPromoReload();
        this.subscribeSimpleMode(
          this.versionPromoService.versionsNotInPromo$,
          this.versionPromoService.totalVersionsNotInPromo$,
          this.versionPromoService.versionsNotInPromoPage$,
          this.versionPromoService.versionsNotInPromoisLoading$,
        );
        break;
      case 'offerGroup':
        this.versionOfferService.offerGdialogsearch('');
        this.versionOfferService.offerGdialogreload();
        this.subscribeSimpleMode(
          this.versionOfferService.offerGDialogVersions$,
          this.versionOfferService.totalofferGDialogVersions$,
          this.versionOfferService.offerGDialogPage$,
          this.versionOfferService.isLoading$,
        );
        break;
      default:
        this.versionOfferService.versionsNotInOfferResetpage();
        this.versionOfferService.dialogSearch('');
        this.versionOfferService.versionsNotInOfferReload();
        this.subscribeSimpleMode(
          this.versionOfferService.versionsNotInOffer$,
          this.versionOfferService.totalVersionsNotInOffer$,
          this.versionOfferService.versionsNotInOfferPage$,
          this.versionOfferService.versionsNotInOfferisLoading$,
        );
        break;
    }
  }

  private subscribeSimpleMode(
    versions$: Observable<Version[]>,
    total$: Observable<number>,
    page$: Observable<PageEvent>,
    loading$: Observable<boolean>,
  ): void {
    this.searchSubscriptions.push(
      versions$.subscribe((versions) => {
        const results = mapPlainVersionsToAttributeSearchResults(
          versions.map((v) => ({
            Id: v.Id,
            VersionName: v.VersionName,
            storesCount: v.storesCount,
          })),
        );
        this.apiResults = results;
        this.searchResultsChange.emit(results);
      }),
      total$.subscribe((total) => {
        this.apiTotal = total;
      }),
      page$.subscribe((page) => {
        this.page = page;
      }),
      loading$.subscribe((loading) => {
        this.attributeSearchLoading = loading;
      }),
    );
  }

  private buildFilterForm(categories: StoreAttributeFilterCategoryViewModel[]): void {
    this.formSubscription?.unsubscribe();
    this.fieldByClientKey.clear();

    const controls: Record<string, FormControl> = {};
    categories.forEach((category) => {
      category.fields.forEach((field) => {
        this.fieldByClientKey.set(field.clientKey, field);
        controls[field.clientKey] = new FormControl(this.defaultValueForField(field));
      });
    });

    this.filterForm = new FormGroup(controls);
    this.formSubscription = this.filterForm.valueChanges.subscribe((values) => {
      this.activeSelections = this.mapFormToSelections(values);
    });
  }

  private defaultValueForField(field: StoreAttributeFilterFieldViewModel): unknown {
    if (field.valueType === 'Boolean') return false;
    if (field.valueType === 'MultiSelect') return [];
    return null;
  }

  private mapFormToSelections(values: Record<string, unknown>): VersionAttributeFilterSelection[] {
    const selections: VersionAttributeFilterSelection[] = [];

    this.fieldByClientKey.forEach((field, clientKey) => {
      const value = values[clientKey];

      if (field.valueType === 'Boolean') {
        if (!value) return;
        selections.push({
          clientKey,
          label: resolveFilterFieldDisplayName(field),
          displayValue: 'Yes',
          categoryColor: field.categoryColor,
        });
        return;
      }

      if (field.valueType === 'MultiSelect') {
        const selected = Array.isArray(value) ? value : [];
        if (!selected.length) return;
        selections.push({
          clientKey,
          label: resolveFilterFieldDisplayName(field),
          displayValue: selected.map((item) => this.getOptionLabel(field, item)).join(', '),
          categoryColor: field.categoryColor,
        });
        return;
      }

      if (field.valueType === 'Text') {
        const text = value == null ? '' : String(value).trim();
        if (!text) return;
        selections.push({
          clientKey,
          label: resolveFilterFieldDisplayName(field),
          displayValue: text,
          categoryColor: field.categoryColor,
        });
        return;
      }

      if (value === null || value === undefined || value === '') return;

      selections.push({
        clientKey,
        label: resolveFilterFieldDisplayName(field),
        displayValue: this.getOptionLabel(field, value),
        categoryColor: field.categoryColor,
      });
    });

    return selections;
  }
}
